/**
 * AssessmentEditor Component
 * 
 * Form for creating and editing assessments with questions.
 * Similar to QuizEditor but tailored for formal assessments.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Paper,
    Grid,
    FormControlLabel,
    Switch,
    MenuItem,
    Divider,
    IconButton,
    Card,
    CardContent,
    Alert,
    Chip,
    CircularProgress
} from '@mui/material';
import {
    Save as SaveIcon,
    Cancel as CancelIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon,
    AutoFixHigh as AIIcon
} from '@mui/icons-material';
import {
    AdminAssessmentDTO,
    AssessmentQuestionDTO,
    generateAssessmentQuestions
} from '../../services/adminAssessmentService';

interface AssessmentEditorProps {
    topicId: number;
    subjectId?: number;
    existingAssessment?: AdminAssessmentDTO;
    onSave: (assessment: Partial<AdminAssessmentDTO>) => Promise<void>;
    onCancel: () => void;
}

/**
 * AssessmentEditor - Create or edit assessments
 * 
 * Features:
 * - Assessment metadata editing
 * - Question management (add, edit, delete, reorder)
 * - AI-powered question generation
 * - Validation before save
 */
const AssessmentEditor: React.FC<AssessmentEditorProps> = ({
    topicId,
    subjectId,
    existingAssessment,
    onSave,
    onCancel
}) => {
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [formData, setFormData] = useState<Partial<AdminAssessmentDTO>>({
        title: '',
        description: '',
        topicId,
        durationMinutes: 60,
        passingScore: 70,
        maxAttempts: 1,
        requiresWorkSubmission: false,
        allowReassessment: true,
        status: 'DRAFT',
        questions: []
    });

    // AI generation config
    const [aiConfig, setAiConfig] = useState({
        questionCount: 5,
        difficultyLevel: 'MEDIUM',
        questionTypes: ['MULTIPLE_CHOICE', 'SHORT_ANSWER'],
        additionalInstructions: ''
    });

    useEffect(() => {
        if (existingAssessment) {
            setFormData({
                ...existingAssessment,
                questions: existingAssessment.questions || []
            });
        }
    }, [existingAssessment]);

    /**
     * Handle form field changes
     */
    const handleFieldChange = (field: keyof AdminAssessmentDTO, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    /**
     * Add a new empty question
     */
    const addQuestion = () => {
        const newQuestion: AssessmentQuestionDTO = {
            questionType: 'MULTIPLE_CHOICE',
            questionText: '',
            options: ['', '', '', ''],
            correctAnswer: '',
            points: 1,
            orderIndex: (formData.questions?.length || 0) + 1
        };
        setFormData(prev => ({
            ...prev,
            questions: [...(prev.questions || []), newQuestion]
        }));
    };

    /**
     * Update a question
     */
    const updateQuestion = (index: number, updates: Partial<AssessmentQuestionDTO>) => {
        setFormData(prev => ({
            ...prev,
            questions: prev.questions?.map((q, i) => i === index ? { ...q, ...updates } : q)
        }));
    };

    /**
     * Delete a question
     */
    const deleteQuestion = (index: number) => {
        if (window.confirm('Delete this question?')) {
            setFormData(prev => ({
                ...prev,
                questions: prev.questions?.filter((_, i) => i !== index)
            }));
        }
    };

    /**
     * Handle AI question generation
     */
    const handleGenerateQuestions = async () => {
        setGenerating(true);
        setError(null);
        try {
            const generatedQuestions = await generateAssessmentQuestions(topicId, aiConfig);
            setFormData(prev => ({
                ...prev,
                questions: [...(prev.questions || []), ...generatedQuestions]
            }));
        } catch (err) {
            console.error('Failed to generate questions', err);
            setError('Failed to generate questions. Please try again.');
        } finally {
            setGenerating(false);
        }
    };

    /**
     * Validate form before saving
     */
    const validateForm = (): string | null => {
        if (!formData.title?.trim()) {
            return 'Assessment title is required';
        }
        if (!formData.passingScore || formData.passingScore < 0 || formData.passingScore > 100) {
            return 'Passing score must be between 0 and 100';
        }
        if (!formData.maxAttempts || formData.maxAttempts < 1) {
            return 'Maximum attempts must be at least 1';
        }
        return null;
    };

    /**
     * Handle save
     */
    const handleSave = async () => {
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await onSave({
                ...formData,
                questionCount: formData.questions?.length || 0
            });
        } catch (err) {
            setError('Failed to save assessment');
        } finally {
            setSaving(false);
        }
    };

    /**
     * Render question editor based on type
     */
    const renderQuestionEditor = (question: AssessmentQuestionDTO, index: number) => {
        return (
            <Card variant="outlined" sx={{ mb: 2 }} key={index}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <DragIcon sx={{ color: 'text.secondary', cursor: 'grab' }} />
                            <Chip label={`Q${index + 1}`} size="small" color="primary" />
                            <Chip label={question.questionType.replace('_', ' ')} size="small" variant="outlined" />
                        </Box>
                        <IconButton size="small" color="error" onClick={() => deleteQuestion(index)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                label="Question Text"
                                multiline
                                rows={2}
                                fullWidth
                                value={question.questionText}
                                onChange={(e) => updateQuestion(index, { questionText: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                select
                                label="Type"
                                fullWidth
                                value={question.questionType}
                                onChange={(e) => updateQuestion(index, { questionType: e.target.value as any })}
                            >
                                <MenuItem value="MULTIPLE_CHOICE">Multiple Choice</MenuItem>
                                <MenuItem value="TRUE_FALSE">True/False</MenuItem>
                                <MenuItem value="SHORT_ANSWER">Short Answer</MenuItem>
                                <MenuItem value="LONG_ANSWER">Long Answer</MenuItem>
                                <MenuItem value="FILE_UPLOAD">File Upload</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextField
                                label="Points"
                                type="number"
                                fullWidth
                                value={question.points}
                                onChange={(e) => updateQuestion(index, { points: parseInt(e.target.value) || 1 })}
                            />
                        </Grid>

                        {/* Multiple Choice Options */}
                        {question.questionType === 'MULTIPLE_CHOICE' && (
                            <>
                                {question.options?.map((option, optIndex) => (
                                    <Grid item xs={12} md={6} key={optIndex}>
                                        <TextField
                                            label={`Option ${optIndex + 1}`}
                                            fullWidth
                                            value={option}
                                            onChange={(e) => {
                                                const newOptions = [...(question.options || [])];
                                                newOptions[optIndex] = e.target.value;
                                                updateQuestion(index, { options: newOptions });
                                            }}
                                        />
                                    </Grid>
                                ))}
                                <Grid item xs={12}>
                                    <TextField
                                        select
                                        label="Correct Answer"
                                        fullWidth
                                        value={question.correctAnswer || ''}
                                        onChange={(e) => updateQuestion(index, { correctAnswer: e.target.value })}
                                    >
                                        {question.options?.map((option, optIndex) => (
                                            <MenuItem key={optIndex} value={option || `Option ${optIndex + 1}`}>
                                                {option || `Option ${optIndex + 1}`}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            </>
                        )}

                        {/* True/False */}
                        {question.questionType === 'TRUE_FALSE' && (
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    label="Correct Answer"
                                    fullWidth
                                    value={question.correctAnswer || ''}
                                    onChange={(e) => updateQuestion(index, { correctAnswer: e.target.value })}
                                >
                                    <MenuItem value="true">True</MenuItem>
                                    <MenuItem value="false">False</MenuItem>
                                </TextField>
                            </Grid>
                        )}

                        {/* Short Answer */}
                        {question.questionType === 'SHORT_ANSWER' && (
                            <Grid item xs={12}>
                                <TextField
                                    label="Expected Answer (for auto-grading)"
                                    fullWidth
                                    value={question.correctAnswer || ''}
                                    onChange={(e) => updateQuestion(index, { correctAnswer: e.target.value })}
                                    helperText="Enter keywords or exact answer for automatic grading"
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <TextField
                                label="Explanation (shown after submission)"
                                multiline
                                rows={2}
                                fullWidth
                                value={question.explanation || ''}
                                onChange={(e) => updateQuestion(index, { explanation: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">
                    {existingAssessment ? 'Edit Assessment' : 'Create New Assessment'}
                </Typography>
                <Box>
                    <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={onCancel}
                        sx={{ mr: 1 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Assessment'}
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Assessment Details */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Assessment Details</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <TextField
                            label="Title"
                            fullWidth
                            required
                            value={formData.title}
                            onChange={(e) => handleFieldChange('title', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            label="Status"
                            fullWidth
                            value={formData.status}
                            onChange={(e) => handleFieldChange('status', e.target.value)}
                        >
                            <MenuItem value="DRAFT">Draft</MenuItem>
                            <MenuItem value="PUBLISHED">Published</MenuItem>
                            <MenuItem value="ARCHIVED">Archived</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Description"
                            multiline
                            rows={3}
                            fullWidth
                            value={formData.description}
                            onChange={(e) => handleFieldChange('description', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Duration (minutes)"
                            type="number"
                            fullWidth
                            value={formData.durationMinutes}
                            onChange={(e) => handleFieldChange('durationMinutes', parseInt(e.target.value) || 0)}
                            helperText="0 = unlimited"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Passing Score (%)"
                            type="number"
                            fullWidth
                            required
                            value={formData.passingScore}
                            onChange={(e) => handleFieldChange('passingScore', parseInt(e.target.value) || 0)}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Max Attempts"
                            type="number"
                            fullWidth
                            required
                            value={formData.maxAttempts}
                            onChange={(e) => handleFieldChange('maxAttempts', parseInt(e.target.value) || 1)}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Scheduled Date"
                            type="datetime-local"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={formData.scheduledDate ? formData.scheduledDate.slice(0, 16) : ''}
                            onChange={(e) => handleFieldChange('scheduledDate', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.requiresWorkSubmission || false}
                                    onChange={(e) => handleFieldChange('requiresWorkSubmission', e.target.checked)}
                                />
                            }
                            label="Requires Work Submission"
                        />
                        <Typography variant="caption" display="block" color="text.secondary">
                            Students must upload files (images, documents)
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.allowReassessment || false}
                                    onChange={(e) => handleFieldChange('allowReassessment', e.target.checked)}
                                />
                            }
                            label="Allow Reassessment"
                        />
                        <Typography variant="caption" display="block" color="text.secondary">
                            Students can request retake after failing
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* AI Question Generation */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
                <Typography variant="h6" gutterBottom>
                    <AIIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    AI Question Generation
                </Typography>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} md={2}>
                        <TextField
                            label="# of Questions"
                            type="number"
                            fullWidth
                            value={aiConfig.questionCount}
                            onChange={(e) => setAiConfig(prev => ({ ...prev, questionCount: parseInt(e.target.value) || 5 }))}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            select
                            label="Difficulty"
                            fullWidth
                            value={aiConfig.difficultyLevel}
                            onChange={(e) => setAiConfig(prev => ({ ...prev, difficultyLevel: e.target.value }))}
                        >
                            <MenuItem value="EASY">Easy</MenuItem>
                            <MenuItem value="MEDIUM">Medium</MenuItem>
                            <MenuItem value="HARD">Hard</MenuItem>
                            <MenuItem value="MIXED">Mixed</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <TextField
                            label="Additional Instructions"
                            fullWidth
                            placeholder="e.g., Focus on practical applications"
                            value={aiConfig.additionalInstructions}
                            onChange={(e) => setAiConfig(prev => ({ ...prev, additionalInstructions: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={generating ? <CircularProgress size={16} /> : <AIIcon />}
                            onClick={handleGenerateQuestions}
                            disabled={generating}
                        >
                            {generating ? 'Generating...' : 'Generate Questions'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Questions Section */}
            <Paper sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6">
                        Questions ({formData.questions?.length || 0})
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={addQuestion}
                    >
                        Add Question
                    </Button>
                </Box>

                {formData.questions?.length === 0 && (
                    <Alert severity="info">
                        No questions added yet. Use AI generation above or add questions manually.
                    </Alert>
                )}

                {formData.questions?.map((question, index) => renderQuestionEditor(question, index))}
            </Paper>
        </Box>
    );
};

export default AssessmentEditor;
